import { useState, useEffect } from "react";
import { NavBar } from "@/components/NavBar";
import { HeroSection } from "@/components/landing/HeroSection";
import { XervenFeatureSection } from "@/components/landing/CultryPayFeatureSection";
import { BusinessTypesSection } from "@/components/landing/BusinessTypesSection";
import { HowItWorksSection } from "@/components/landing/HowItWorksSection";
import { PricingSection } from "@/components/landing/PricingSection";
import { Footer } from "@/components/landing/Footer";
import { DemoRequestDialog } from "@/components/landing/DemoRequestDialog";
import { Blog } from "@/types/blog";
import { fetchPublishedBlogs } from "@/services/blog/blogService";
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Heart, User } from "lucide-react";
import { useTranslation } from "react-i18next";

const Index = () => {
  const { t } = useTranslation();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        setLoading(true);
        const fetchedBlogs = await fetchPublishedBlogs();
        setBlogs(fetchedBlogs.slice(0, 3)); // Get the 3 most recent blogs
      } catch (err) {
        console.error("Failed to load blogs:", err);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-1">
        <HeroSection />

        {/* New Business Types Section */}
        <BusinessTypesSection />

        <XervenFeatureSection />

        <HowItWorksSection />

        <PricingSection />

        {/* Featured Blog Posts Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-8">
              {t("index.featuredBlogsTitle")}
            </h2>
            <p className="text-center mb-8">
              {t("index.featuredBlogsDescription")}
            </p>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <Card key={i} className="animate-pulse">
                    <div className="h-48 bg-muted rounded-t-lg"></div>
                    <CardContent className="p-6">
                      <div className="h-6 bg-muted rounded mb-2 w-3/4"></div>
                      <div className="h-4 bg-muted rounded mb-1 w-1/2"></div>
                      <div className="h-16 bg-muted rounded mt-4"></div>
                    </CardContent>
                    <CardFooter className="p-6 pt-0">
                      <div className="h-10 bg-muted rounded w-full"></div>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {blogs.map((blog, index) => (
                  <Card
                    key={blog.id}
                    className="flex flex-col h-full overflow-hidden hover:shadow-lg transition-all duration-300 hover:translate-y-[-5px] animate-fade-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {blog.coverImage && (
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={blog.coverImage}
                          alt={blog.title}
                          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                        />
                      </div>
                    )}

                    <CardContent className="p-6 flex-grow">
                      <div className="flex items-center text-muted-foreground text-xs space-x-3 mb-3">
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          <span>
                            {new Date(blog.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Heart className="h-3 w-3 mr-1" />
                          <span>{blog.upvotes}</span>
                        </div>
                        {blog.authorName && (
                          <div className="flex items-center">
                            <User className="h-3 w-3 mr-1" />
                            <span>{blog.authorName}</span>
                          </div>
                        )}
                      </div>

                      <h2 className="text-xl font-bold mb-2 line-clamp-2">
                        {blog.title}
                      </h2>
                      <p className="text-muted-foreground mb-4 line-clamp-3">
                        {blog.summary}
                      </p>
                    </CardContent>

                    <CardFooter className="p-6 pt-0">
                      <Button asChild className="w-full">
                        <Link to={`/blog/${blog.slug}`}>
                          {t("index.readArticle")}
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}

            <div className="text-center mt-10">
              <Button variant="outline" asChild>
                <Link to="/blog">{t("index.viewAllArticles")}</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-16 bg-gradient-to-br from-cultry-primary to-cultry-secondary text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t("index.cta.title")}
            </h2>
            <p className="text-lg mb-8 opacity-90">
              {t("index.cta.description")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="outline" asChild>
                <Link to="/register">{t("index.cta.startFreeTrial")}</Link>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default Index;
